import React, { useState, useEffect } from 'react';
import {
  Paper,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useGlobal } from '../contexts/GlobalContext';
import { useBuild } from '../contexts/BuildContext';
import { findIndex } from '../indexUtils';


const FeaturesQueryColumn = () => {
  const { defaultFarcasterUserId, defaultPolymarketWalletAddress, mergeCurrentContext } = useGlobal();
  const { hits, featuresApi, isLoading, setHits } = useBuild();
  
  const [version, setVersion] = useState('v1');
  const [userType, setUserType] = useState('polymarket-wallets');
  const [userId, setUserId] = useState('');

  useEffect(() => {
    mergeCurrentContext({
      featuresVersion: version,
      featuresUserType: userType,
      featuresUserId: userId || undefined,
    });
    return () => mergeCurrentContext({ featuresVersion: undefined, featuresUserType: undefined, featuresUserId: undefined });
  }, [version, userType, userId, mergeCurrentContext]);

  // Set default user ID when component mounts or when userType changes
  useEffect(() => {
    if (userType === 'farcaster-profiles' && defaultFarcasterUserId) {
      setUserId(defaultFarcasterUserId);
    } else if (userType === 'polymarket-wallets' && defaultPolymarketWalletAddress) {
      setUserId(defaultPolymarketWalletAddress);
    } else {
      setUserId('');
    }
  }, [userType, defaultFarcasterUserId, defaultPolymarketWalletAddress]);

  

  const handleExecuteQuery = async () => {
    if (!userId.trim()) {
      return;
    }

    try {
      const payload = {
        user: {
          index: userType,
          id: userId,
        },
      };
      if (!hits || hits.length === 0) {
        return;
      }
      const items = hits.map((hit) => ({index: hit._index, id: hit._id}));
      payload.items = items;
      console.log('Sending Features Query:', version, payload);
      const response = await featuresApi.getFeatures(version, payload);
      console.log('API Response:', response);
      const features = response?.result?.features ;
      const scores = response?.result?.scores ;
      const info = response?.result?.info ;
      if (!features && !scores && !info) {
        return;
      }
      for (const hit of hits) {
        const hitIndex = findIndex(hit._index);
        const hitFeatures = features?.[hitIndex]?.[hit._id];
        const hitScores = scores?.[hitIndex]?.[hit._id];
        const hitInfo = info?.[hitIndex]?.[hit._id];
        if (hit._features) {
          hit._features = {
            ...hit._features,
            ...hitFeatures,
          };
        } else {
          hit._features = hitFeatures;
        }
        if (hit._scores) {
          hit._scores = {
            ...hit._scores,
            ...hitScores,
          };
        } else {
          hit._scores = hitScores;
        }
        if (hit._info) {
          hit._info = {
            ...hit._info,
            ...hitInfo,
          };
        } else {
          hit._info = hitInfo;
        }
      }
      setHits([...hits]);
    } catch (error) {
      console.error('Error fetching features:', error);
    }
  };

  const isQueryValid = userId.trim().length > 0;

  return (
    <Paper elevation={2} sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Compose Query
      </Typography>
      
      <Stack spacing={2} sx={{ flex: 1 }}>
        {/* Version Input */}
        <FormControl fullWidth size="small" variant="outlined">
          <InputLabel>Version</InputLabel>
          <Select
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            label="Version"
            sx={{
              bgcolor: 'background.paper',
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  bgcolor: 'background.paper',
                },
              },
            }}
          >
            <MenuItem value="v1">v1</MenuItem>
          </Select>
        </FormControl>

        {/* User Type Input */}
        <FormControl fullWidth size="small" variant="outlined">
          <InputLabel>User Type</InputLabel>
          <Select
            value={userType}
            onChange={(e) => setUserType(e.target.value)}
            label="User Type"
            sx={{
              bgcolor: 'background.paper',
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  bgcolor: 'background.paper',
                },
              },
            }}
          >
            <MenuItem value="farcaster-profiles">farcaster-profiles</MenuItem>
            <MenuItem value="zora-profiles">zora-profiles</MenuItem>
            <MenuItem value="polymarket-wallets">polymarket-wallets</MenuItem>
          </Select>
        </FormControl>

        {/* User Id Input */}
        <TextField
          label="User ID"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          size="small"
          fullWidth
          variant="outlined"
          placeholder="Enter user ID"
          sx={{ bgcolor: 'background.paper' }}
        />

        {/* Execute Query Button */}
        <Box sx={{ mt: 'auto' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExecuteQuery}
            disabled={!isQueryValid || isLoading}
            fullWidth
          >
            {isLoading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Executing...
              </>
            ) : (
              'Execute Query'
            )}
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
};

export default FeaturesQueryColumn;

